import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssistantAnswer } from '../lib/assistant.js';
import { downloadCsv, toCsv } from '../lib/csv.js';

/** Generic follow-ups offered after an answer to keep the investigation going. */
const FOLLOW_UPS = [
  'Break that down by carrier',
  'Show the dollar impact',
  'How has this trended over the last 6 months?',
  'Who are the top 5?',
];

export function AnswerView({
  answer,
  onAsk,
  onSave,
  onWatch,
  onInvestigate,
  showFollowUps,
}: {
  answer: AssistantAnswer;
  onAsk: (q: string) => void;
  onSave: (a: AssistantAnswer) => void;
  onWatch: (question: string) => void;
  onInvestigate: (question: string) => void;
  showFollowUps: boolean;
}) {
  const [showSql, setShowSql] = useState(false);
  const canSave = (answer.intent ?? 'book_query') === 'book_query' && !!answer.sql;
  const rows = answer.rows ?? [];
  const columns = answer.columns ?? [];
  const citations = answer.citations ?? [];
  const hasRows = rows.length > 0;

  const exportCsv = () => {
    const cols = columns.map((c) => ({ key: c, header: humanize(c) }));
    downloadCsv('assistant-results.csv', toCsv(rows, cols));
  };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm leading-relaxed text-neutral-800 shadow-sm dark:bg-neutral-900 dark:text-neutral-100">
        {answer.answer}
      </div>

      {/* Investigation trace — what the assistant actually checked. */}
      {answer.intent === 'investigate' && (answer.steps?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
          <span className="font-medium">Checked:</span>
          {answer.steps?.map((s, i) => (
            <span
              key={`${s.summary}-${i}`}
              className={`rounded-full border px-2 py-0.5 ${s.ok ? 'border-neutral-200 dark:border-neutral-700' : 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'}`}
            >
              {s.summary}
            </span>
          ))}
        </div>
      )}

      {/* Offer the deep dive on quick data answers. */}
      {(answer.intent === 'book_query' || answer.intent === 'recovery_query') &&
        answer.question && (
          <button
            type="button"
            onClick={() => onInvestigate(answer.question)}
            title="Run a multi-step investigation: several queries and reports, cross-checked, before answering"
            className="inline-flex w-fit rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-200 dark:hover:border-emerald-600/60 dark:hover:text-emerald-300"
          >
            🔍 Investigate deeper
          </button>
        )}

      {/* Recovery answers link to the full report + can become a standing watch. */}
      {answer.intent === 'recovery_query' && answer.reportPath && (
        <div className="flex flex-wrap gap-2">
          <Link
            to={answer.reportPath}
            className="inline-flex w-fit rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-200 dark:hover:border-emerald-600/60 dark:hover:text-emerald-300"
          >
            Open the full report →
          </Link>
          <button
            type="button"
            onClick={() => onWatch(answer.question)}
            title="Re-run this question after every statement and email you if the count grows"
            className="inline-flex w-fit rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-200 dark:hover:border-emerald-600/60 dark:hover:text-emerald-300"
          >
            ⏱ Watch this
          </button>
        </div>
      )}

      {/* "Do it for me" → send them to the live AI carrier-setup flow. */}
      {answer.intent === 'action' && (
        <Link
          to="/carrier-setup"
          className="inline-flex w-fit rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700"
        >
          Set up a carrier with AI →
        </Link>
      )}

      {/* Product-support answers cite the docs they came from. */}
      {citations.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
          <span className="font-medium">Sources:</span>
          {citations.map((cite) => (
            <a
              key={cite.url}
              href={cite.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2.5 py-0.5 text-neutral-600 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-emerald-600/60 dark:hover:text-emerald-300"
            >
              {cite.title}
              <span aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      )}

      {hasRows && (
        <>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Export CSV
            </button>
          </div>
          <ResultTable columns={columns} rows={rows} />
        </>
      )}
      {answer.truncated && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          Showing the first {rows.length} rows.
        </p>
      )}

      {showFollowUps &&
        ['book_query', 'recovery_query'].includes(answer.intent ?? 'book_query') && (
          <div className="flex flex-wrap gap-2 pt-1">
            {(answer.followUps?.length ? answer.followUps : FOLLOW_UPS).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => onAsk(q)}
                className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600 transition hover:border-emerald-400 hover:bg-emerald-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-emerald-600/60 dark:hover:bg-emerald-950/30"
              >
                {q}
              </button>
            ))}
          </div>
        )}

      {answer.sql && (
        <div>
          <div className="flex items-center gap-4">
            {canSave && (
              <button
                type="button"
                onClick={() => onSave(answer)}
                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
              >
                Save as report
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowSql((s) => !s)}
              className="text-xs font-medium text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
            >
              {showSql ? 'Hide query' : 'View query'}
            </button>
          </div>
          {showSql && (
            <pre className="mt-1 overflow-x-auto rounded-lg bg-neutral-900 p-3 text-[11px] leading-relaxed text-emerald-200">
              {answer.sql}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function ResultTable({ columns, rows }: { columns: string[]; rows: Record<string, unknown>[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
      <table className="w-full text-left text-xs">
        <thead className="bg-neutral-100 text-neutral-500 dark:bg-neutral-800/60">
          <tr>
            {columns.map((col) => (
              <th key={col} className="whitespace-nowrap px-3 py-2 font-medium">
                {humanize(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id
            <tr key={i} className="bg-white dark:bg-neutral-900">
              {columns.map((col) => (
                <td
                  key={col}
                  className="whitespace-nowrap px-3 py-1.5 text-neutral-700 dark:text-neutral-300"
                >
                  {formatCell(col, row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MONEY_RE = /(commission|premium|amount|total|revenue)/i;

function formatCell(col: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number' && MONEY_RE.test(col)) {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  if (typeof value === 'number') return value.toLocaleString('en-US');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function humanize(col: string): string {
  return col.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}
